import classNames from 'classnames';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    InputHTMLAttributes,
    ReactNode,
    ForwardRefRenderFunction,
} from 'react';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
    autoFocus?: boolean;
    icon?: ReactNode;
    iconContainerClassName?: string;
    containerClassName?: string;
};

export type TextInputHandle = {
    focus: () => void;
    select: () => void;
    setValue: (value: string) => void;
};

const TextInputRenderFunc: ForwardRefRenderFunction<
    TextInputHandle,
    TextInputProps
> = (
    {
        type = 'text',
        className = '',
        autoFocus = false,
        icon,
        iconContainerClassName,
        containerClassName,
        ...props
    },
    ref,
) => {
    const localRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
        select: () => localRef.current?.select(),
        setValue: (value: string) => {
            if (localRef.current) {
                localRef.current.value = value;
            }
        },
    }));

    useEffect(() => {
        if (autoFocus) {
            localRef.current?.focus();
        }
    }, []);

    const content = (
        <input
            {...props}
            type={type}
            className={
                'border-brand-teal border-2 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/[0.35] rounded text-brand-blue disabled:text-gray-40 disabled:bg-gray-200 disabled:border-gray-300 disabled:cursor-not-allowed py-2.5 px-4 ' +
                className
            }
            ref={localRef}
        />
    );

    return icon ? (
        <div className={classNames('relative w-fit', containerClassName)}>
            {content}
            <div
                className={classNames(
                    'absolute h-full top-0 right-0 pointer-events-none px-2 flex items-center justify-center',
                    iconContainerClassName,
                )}>
                {icon}
            </div>
        </div>
    ) : (
        content
    );
};

export default forwardRef(TextInputRenderFunc);
